import { forwardRef, useEffect, useRef, useState, type ReactElement, type ReactNode } from 'react'
import { AlertDialog as BaseAlertDialog } from '@base-ui/react/alert-dialog'
import { Dialog as BaseDialog } from '@base-ui/react/dialog'
import { CircleAlert, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button, IconButton, type ButtonProps } from './Button'
import './dialog.css'

export type DialogSize = 'default' | 'wide' | 'media'

/** The content every Dialog and Drawer shares. */
export type DialogContentProps = {
  /** Required. Names the dialog for everyone, including assistive technology. */
  title: ReactNode
  /** A short line above the title that places it, such as the event or the camera. It is read as part of the name. */
  eyebrow?: ReactNode
  /** One short line under the title. Also read when the dialog opens. */
  description?: ReactNode
  /** Visually hides the description; it still describes the dialog. For a hint the layout already makes plain. */
  hideDescription?: boolean
  /** Visually hides the title and description; they still name and describe it. For media that speaks for itself. */
  hideTitle?: boolean
  /** Actions, right-aligned. The primary action goes last. */
  footer?: ReactNode
  /** The body. It scrolls when taller than the screen while the title and footer stay put. */
  children?: ReactNode
}

/** True once the element's content is taller than the element, so keyboard users can focus and scroll it. */
function useScrollable() {
  const [node, setNode] = useState<HTMLDivElement | null>(null)
  const [scrollable, setScrollable] = useState(false)
  useEffect(() => {
    if (!node || typeof ResizeObserver === 'undefined') return
    const update = () => setScrollable(node.scrollHeight > node.clientHeight + 1)
    const observer = new ResizeObserver(update)
    observer.observe(node)
    Array.from(node.children).forEach((child) => observer.observe(child))
    update()
    return () => observer.disconnect()
  }, [node])
  return [setNode, scrollable] as const
}

/** Header, scrolling body, footer and close button. Shared by Dialog and Drawer; use those instead. */
export function DialogLayout({ title, eyebrow, description, hideDescription = false, hideTitle = false, footer, children }: DialogContentProps) {
  const [bodyRef, scrollable] = useScrollable()
  return (
    <>
      <div className={hideTitle ? 'sr-only' : 'play-dialog__header'}>
        <BaseDialog.Title className="play-dialog__title">
          {/* Inside the heading, so the dialog's name reads “Camera invitation Summit finish”; the space keeps the words apart. */}
          {eyebrow && <><span className="play-dialog__eyebrow">{eyebrow}</span>{' '}</>}
          {title}
        </BaseDialog.Title>
        {description && (
          <BaseDialog.Description className={cn('play-dialog__description', hideDescription && 'sr-only')}>{description}</BaseDialog.Description>
        )}
      </div>
      {children != null && children !== false && (
        <div ref={bodyRef} className="play-dialog__body" tabIndex={scrollable ? 0 : undefined}>{children}</div>
      )}
      {footer && <div className="play-dialog__footer">{footer}</div>}
      {/* Last in the DOM so opening focuses the first field or action, not Close; it still sits top right. No tooltip:
          the corner X is universal, and a tooltip on the first focused control would swallow the first Escape. */}
      <BaseDialog.Close render={<IconButton label="Close" variant="ghost" tooltip={false} className="play-dialog__close"><X /></IconButton>} />
    </>
  )
}

type DialogRootProps = Pick<BaseDialog.Root.Props, 'open' | 'defaultOpen' | 'onOpenChange' | 'onOpenChangeComplete' | 'disablePointerDismissal'>

export type DialogProps = DialogRootProps & DialogContentProps & Omit<BaseDialog.Popup.Props, 'title' | 'children' | 'className' | 'render'> & {
  /** Opens the dialog, for example a Button. Leave it out when something else controls `open`. */
  trigger?: ReactElement
  /** Width: default for forms and short content, wide for longer forms, media for a camera preview or map. */
  size?: DialogSize
  /** Portal target, for example an active fullscreen root. Defaults to the document body. */
  container?: HTMLElement | null
  /** Classes for the dialog surface. */
  className?: string
}

/** A focused task over the page. Focus moves inside, the page behind is inert, and Escape or Close returns. */
export const Dialog = forwardRef<HTMLDivElement, DialogProps>(function Dialog(
  {
    open, defaultOpen, onOpenChange, onOpenChangeComplete, disablePointerDismissal, trigger,
    title, eyebrow, description, hideDescription, hideTitle = false, footer, children, size = 'default', container, className, ...popup
  },
  ref,
) {
  return (
    <BaseDialog.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      onOpenChangeComplete={onOpenChangeComplete}
      disablePointerDismissal={disablePointerDismissal}
    >
      {trigger && <BaseDialog.Trigger render={trigger} />}
      <BaseDialog.Portal container={container ?? undefined}>
        <BaseDialog.Backdrop className="play-dialog__backdrop" />
        <BaseDialog.Viewport className="play-dialog__viewport">
          <BaseDialog.Popup
            ref={ref}
            className={cn('play-theme play-dialog', size !== 'default' && `play-dialog--${size}`, hideTitle && 'play-dialog--title-hidden', className)}
            {...popup}
          >
            <DialogLayout title={title} eyebrow={eyebrow} description={description} hideDescription={hideDescription} hideTitle={hideTitle} footer={footer}>
              {children}
            </DialogLayout>
          </BaseDialog.Popup>
        </BaseDialog.Viewport>
      </BaseDialog.Portal>
    </BaseDialog.Root>
  )
})

export type DialogCloseProps = ButtonProps

/** A footer button that closes its Dialog or Drawer, such as Cancel or Done. Ghost unless you set a variant. */
export const DialogClose = forwardRef<HTMLButtonElement, DialogCloseProps>(function DialogClose({ variant = 'ghost', ...props }, ref) {
  return <BaseDialog.Close ref={ref} render={<Button variant={variant} {...props} />} />
})

export type ConfirmDialogProps = Pick<BaseAlertDialog.Root.Props, 'open' | 'defaultOpen' | 'onOpenChange'> & {
  /** Opens the confirmation, for example a danger Button. Leave it out when something else controls `open`. */
  trigger?: ReactElement
  /** Required. Ask about the result: “Delete Summit finish?” */
  title: ReactNode
  /** Name what will be lost and what stays. */
  description?: ReactNode
  /** Required. Repeat the action: “Delete camera”, never “OK” or “Yes”. */
  confirmLabel: string
  cancelLabel?: string
  /** Danger for destructive actions; primary for a consequential but safe one. */
  variant?: 'danger' | 'primary'
  /** Runs the action. Return a promise to show progress; the dialog closes when it resolves and stays open if it rejects. */
  onConfirm: () => void | Promise<unknown>
  /** Shown when `onConfirm` rejects. Say what failed and what to do next. */
  failureMessage?: ReactNode
  /** Portal target, for example an active fullscreen root. Defaults to the document body. */
  container?: HTMLElement | null
  /** Where focus goes when it closes. Defaults to whatever opened it; pass another target when the action removes that. */
  finalFocus?: BaseAlertDialog.Popup.Props['finalFocus']
  /** Classes for the dialog surface. */
  className?: string
}

/** Confirms a destructive or irreversible action. It closes only by choosing, Cancel or Escape. */
export function ConfirmDialog({
  open, defaultOpen, onOpenChange, trigger, title, description, confirmLabel, cancelLabel = 'Cancel', variant = 'danger',
  onConfirm, failureMessage = 'That didn’t work. Try again.', container, finalFocus, className,
}: ConfirmDialogProps) {
  const actions = useRef<BaseAlertDialog.Root.Actions>(null)
  const pendingRef = useRef(false)
  const [pending, setPending] = useState(false)
  const [failed, setFailed] = useState(false)
  // Each opening starts without the last one's failure: a controlled `open` turning true (the page opening it again
  // after Cancel) as well as its own trigger (onOpenChange, below).
  const [shownOpen, setShownOpen] = useState(open)
  if (open !== shownOpen) {
    setShownOpen(open)
    if (open) setFailed(false)
  }

  const settle = (didFail: boolean) => {
    pendingRef.current = false
    setPending(false)
    setFailed(didFail)
  }

  const confirm = async () => {
    setFailed(false)
    try {
      const result = onConfirm()
      if (result && typeof (result as Promise<unknown>).then === 'function') {
        pendingRef.current = true
        setPending(true)
        await result
      }
      settle(false)
      actions.current?.close()
    } catch {
      settle(true)
    }
  }

  return (
    <BaseAlertDialog.Root
      open={open}
      defaultOpen={defaultOpen}
      actionsRef={actions}
      onOpenChange={(next, details) => {
        // Keep the dialog until the action settles, so the outcome is never hidden.
        if (!next && pendingRef.current) {
          details.cancel()
          return
        }
        if (next) setFailed(false)
        onOpenChange?.(next, details)
      }}
    >
      {trigger && <BaseAlertDialog.Trigger render={trigger} />}
      <BaseAlertDialog.Portal container={container ?? undefined}>
        <BaseAlertDialog.Backdrop className="play-dialog__backdrop" />
        <BaseAlertDialog.Viewport className="play-dialog__viewport">
          <BaseAlertDialog.Popup finalFocus={finalFocus} className={cn('play-theme play-dialog play-dialog--compact', className)}>
            <div className="play-dialog__header">
              <BaseAlertDialog.Title className="play-dialog__title">{title}</BaseAlertDialog.Title>
              {description && <BaseAlertDialog.Description className="play-dialog__description">{description}</BaseAlertDialog.Description>}
            </div>
            {failed && (
              <p className="play-dialog__error" role="alert">
                <CircleAlert aria-hidden="true" />
                <span>{failureMessage}</span>
              </p>
            )}
            <div className="play-dialog__footer">
              {/* Cancel comes first, so it takes focus when the dialog opens. */}
              <BaseAlertDialog.Close disabled={pending} render={<Button variant="ghost">{cancelLabel}</Button>} />
              <Button variant={variant} loading={pending} onClick={() => void confirm()}>{confirmLabel}</Button>
            </div>
          </BaseAlertDialog.Popup>
        </BaseAlertDialog.Viewport>
      </BaseAlertDialog.Portal>
    </BaseAlertDialog.Root>
  )
}
